/**
 * SUKANACOOP — Cookie consent (CNIL/RGPD compliant).
 *
 * Feb 2026 — batch 26.
 *
 * Categories (per CNIL guidelines):
 *   - `necessary`  — session, auth token, cart, currency & language preference.
 *                    ALWAYS ON. Exempt from consent because strictly required
 *                    for the service the user asked for.
 *   - `analytics`  — anonymous usage stats to improve the platform.
 *                    Requires opt-in. Currently NO tracker is loaded.
 *   - `marketing`  — retargeting / advertising / social pixels.
 *                    Requires opt-in. Currently NO tracker is loaded.
 *
 * Storage: a JSON blob in localStorage. A `version` field allows us to force a
 * re-consent whenever the policy changes. Expiration is 6 months, per CNIL.
 */

export const COOKIE_STORAGE_KEY = "sukana_cookie_consent";
export const COOKIE_CONSENT_VERSION = 1;
export const COOKIE_CONSENT_MAX_AGE_MS = 6 * 30 * 24 * 60 * 60 * 1000; // ~6 months

export const DEFAULT_CONSENT = Object.freeze({
  necessary: true,   // always on — cannot be turned off
  analytics: false,
  marketing: false,
});

/** Read the current consent from localStorage (or null if none / expired). */
export function readConsent() {
  try {
    const raw = localStorage.getItem(COOKIE_STORAGE_KEY);
    if (!raw) return null;
    const doc = JSON.parse(raw);
    if (doc.version !== COOKIE_CONSENT_VERSION) return null;
    if (doc.expiresAt && Date.now() > doc.expiresAt) return null;
    return {
      necessary: true,
      analytics: !!doc.analytics,
      marketing: !!doc.marketing,
      recordedAt: doc.recordedAt,
    };
  } catch { return null; }
}

/** Persist a consent decision. `choices` is a partial object. */
export function saveConsent(choices) {
  const doc = {
    version: COOKIE_CONSENT_VERSION,
    necessary: true, // strictly-necessary category is always ON
    analytics: !!choices.analytics,
    marketing: !!choices.marketing,
    recordedAt: new Date().toISOString(),
    expiresAt: Date.now() + COOKIE_CONSENT_MAX_AGE_MS,
  };
  localStorage.setItem(COOKIE_STORAGE_KEY, JSON.stringify(doc));
  // Broadcast so any listener can (de)activate its trackers on the fly
  window.dispatchEvent(new CustomEvent("sukana:cookie-consent", { detail: doc }));
  return doc;
}

/** Convenience: accept all optional categories. */
export function acceptAll() { return saveConsent({ analytics: true, marketing: true }); }

/** Convenience: refuse everything except strictly-necessary. */
export function refuseAll() { return saveConsent({ analytics: false, marketing: false }); }

/** Erase any stored consent (forces the banner to reappear). */
export function resetConsent() {
  localStorage.removeItem(COOKIE_STORAGE_KEY);
  window.dispatchEvent(new CustomEvent("sukana:cookie-consent", { detail: null }));
}
